import { withOverrides } from './fxPackages.js';
import { prefersReducedMotion } from './reducedMotion.js';

/*
 * Reduced stream effects (ENG-810, FX-5; ENG-836): what the engine plays for
 * a photosensitive listener. Whether effects are reduced is decided by
 * `effectiveReduceEffects` below, from the account's Photosensitivity mode
 * (one setting on every device, `contexts/AccountFxPreferences.jsx`) and the
 * device's reduce-motion setting. Nothing is stored on the device: the
 * ENG-810 localStorage key `songshake:fx-reduce-effects` is retired and is
 * never read or written.
 *
 * While effects are reduced:
 * - every boost plays without its white flash, its release and tail lightning,
 *   the flash it throws onto neighbouring cards and the plume flicker
 *   (`REDUCED_BOOST`), whatever boost package the booster equipped;
 * - the vibe set draws without flicker or build lightning, and a vote no
 *   longer flashes the card (`REDUCED_VIBE`);
 * - the engine's reduced-motion mode is on (no shake, tremble, push or speed
 *   lines), even when the device does not ask for it.
 * Shared packages are never touched: each override goes into a per-viewer
 * settings copy (`withOverrides`).
 */

const ZERO_LEVELS = Object.freeze([0, 0, 0, 0, 0]);

/** Boost settings that make a boost photosensitivity-safe. */
export const REDUCED_BOOST = Object.freeze({
  flash: 0,
  releaseBolts: 0,
  tailBolts: 0,
  reactFlash: 0,
  plumeFlicker: 0,
});

/** Vibe set settings with the flicker and the lightning taken out. */
export const REDUCED_VIBE = Object.freeze({
  flicker: ZERO_LEVELS,
  boltOn: false,
  ag_flicker: ZERO_LEVELS,
  ag_boltOn: false,
  kickFlash: 0,
});

const REDUCED = Object.freeze({ boost: REDUCED_BOOST, vibe: REDUCED_VIBE });

/** The reduce-effects overrides for a package type ('boost' | 'vibe'), or null for any other type. */
export const reducedOverridesFor = (type) => REDUCED[type] ?? null;

const isPlainObject = (x) => Boolean(x) && typeof x === 'object' && !Array.isArray(x);

/**
 * What to hand the engine in place of `pkg` (anything `card.boost` or
 * `stage.setVibe` takes: a package id, a package, a compiled package, a plain
 * settings object or null) so it plays with the reduce-effects overrides of
 * `type`. A fresh object; the registered package is not touched. An id the
 * engine does not know is passed through unchanged, so it fails as before.
 */
export const reducePackage = (pkg, type) => {
  const overrides = reducedOverridesFor(type);
  if (!overrides) return pkg;
  if (typeof pkg === 'string') return withOverrides(pkg, type, overrides) ?? pkg;
  if (isPlainObject(pkg) && isPlainObject(pkg.v) && pkg.col) return { ...pkg, v: { ...pkg.v, ...overrides } };
  if (isPlainObject(pkg) && isPlainObject(pkg.settings) && pkg.type) return { ...pkg.settings, ...overrides };
  return { ...(isPlainObject(pkg) ? pkg : {}), ...overrides };
};

/** Whether the device asks for reduced motion (`prefers-reduced-motion: reduce`). */
export { prefersReducedMotion };

/**
 * Whether stream effects play reduced (ENG-836). Effects are reduced when any
 * of these holds:
 * 1. the account's Photosensitivity mode is on (`photosensitivityMode`,
 *    including an optimistic change not yet confirmed);
 * 2. the account state is not known yet (`status` 'loading' or 'error'):
 *    fail safe, so a listener who turned the mode on never gets flashes
 *    because GET /me was slow or failed;
 * 3. the first-login photosensitivity warning is open (`warningOpen`);
 * 4. the device asks for reduced motion (`osReducedMotion`). That one is a
 *    device-only layer: never saved, and it never flips the switch.
 * With no session (`status` 'no-session') there is no account layer, only the
 * device's.
 */
export const effectiveReduceEffects = ({ status, photosensitivityMode, warningOpen, osReducedMotion }) => {
  if (osReducedMotion) return true;
  if (status === 'loading' || status === 'error') return true;
  if (status === 'ready') return Boolean(photosensitivityMode || warningOpen);
  return false;
};
